import { ReviewCard } from '@/components/cards';
import type { ReviewApi } from '@repo/api';
import { SectionHeader } from '@/components/ui/SectionHeader';
import type { ReactNode } from 'react';
import { SectionContainer, SectionContent } from '@/components/layout/Containers';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ReviewsProps {
  reviews: { data: ReviewApi[]; count: number };
  showAddress?: boolean;
  isMasked: boolean;
  onViewAll?: ReactNode; // Custom view all button/modal trigger
  sectionTitle?: string;
  eventScreen?: 'building' | 'contact' | 'other';
}

export function Reviews({
  reviews,
  showAddress = false,
  isMasked,
  onViewAll,
  sectionTitle = 'Reviews',
  eventScreen = 'other',
}: ReviewsProps) {
  const { data: reviewData, count: reviewCount } = reviews;

  // Show max 2 reviews as per design
  const displayReviews = reviewData?.slice(0, 2) || [];

  if (!reviewData || reviewData.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <SectionHeader title={sectionTitle} />
        <div className="section-content text-body-2-normal text-secondary">No reviews yet</div>
      </div>
    );
  }

  return (
    <SectionContainer>
      {/* Section Header */}
      <SectionHeader title={sectionTitle} />

      {/* Reviews Content */}
      <SectionContent>
        {/* Reviews Container */}
        <div className="flex flex-col gap-4">
          {displayReviews.map((review, index) => (
            <ReviewCard
              key={`review-${index}`}
              review={review}
              showAddress={showAddress}
              isMasked={isMasked}
              eventScreen={eventScreen}
              className="p-4 border border-secondary"
            />
          ))}
        </div>

        {/* View All Reviews Button */}
        {reviewCount > 2 && onViewAll && <div className="mt-4">{onViewAll}</div>}
      </SectionContent>
    </SectionContainer>
  );
}
